import React, { useState } from 'react';
import { Modal, Typography, Icon, Spinner, Button } from '@scale/scaleui';
import { ITaskAttemptToReturnJson } from '@scale/shared/types/taskattempt';
import CopyToClipboard from 'react-copy-to-clipboard';
import { getTimeWorkedText, getCurrentClaimTimeForReviewLevel, startChatBulkAudit } from './utils';
import {
  useGetProjectClaimTimeMax,
  useUserTaskInfo,
  useFetchUserInfo,
} from 'react-scale/components/audits/ChatBulkAudit/apiClient';
import { TaskHistoryTable, TaskInfo } from './TaskHistoryTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AuditLookupLink,
  AuditPodLeadLink,
} from 'react-scale/components/audits/ChatBulkAudit/AuditLink';
import _ from 'lodash';
import { getTasksForAttempter } from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditApi';
import { ChatBulkAuditBatch } from '../SpeedAudit/types';
import { useAttempterAuditScores } from './apiClient';

interface ChatBulkAuditHeaderProps {
  open: boolean;
  onClose: () => void;
  contributorId: string;
  taskInfo: TaskInfo[];
  attempt: ITaskAttemptToReturnJson;
  auditBatch: ChatBulkAuditBatch;
  detailType: 'Task' | 'Contributor';
}

// eslint-disable-next-line complexity
function ChatBulkAuditDetails(props: ChatBulkAuditHeaderProps) {
  const { open, onClose, contributorId, attempt, taskInfo, auditBatch } = props;
  const { project: projectId, _id: auditBatchId } = auditBatch;

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { data: podInfo, status: podInfoStatus } = useFetchUserInfo(contributorId);
  const { data: userTaskInfoData, error, isLoading } = useUserTaskInfo(contributorId, attempt);
  const {
    data: claimTimeData,
    error: claimTimeError,
    isLoading: claimTimeLoading,
  } = useGetProjectClaimTimeMax(auditBatchId);
  const currentCapForReviewLevel = getCurrentClaimTimeForReviewLevel(
    claimTimeData,
    attempt.attemptedAtReviewLevel,
  );
  const {
    attempter: contributor,
    sbqPercent,
    sbqRejectedAttempts,
    sbqTotalAttempts,
  } = userTaskInfoData ?? {};

  const { data: auditScoreData, isLoading: auditScoreLoading } = useAttempterAuditScores(
    projectId,
    contributorId,
  );
  const { auditAverage, numAuditedAttempts } = auditScoreData ?? {};

  const createAudit = async () => {
    // TODO: Implement function to create audit for this contributor
    try {
      setSubmitting(true);
      setErrorMessage('');

      if (!projectId) {
        setErrorMessage('No project ID found');
        return;
      }

      const { attemptIds } = await getTasksForAttempter(projectId, contributorId, 5);
      if (!attemptIds || attemptIds.length === 0) {
        setErrorMessage('No tasks in project to audit');
        return;
      } else {
        const date = new Date();
        let name = ((contributor?.firstName ?? '') + ' ' + (contributor?.lastName ?? '')).trim();
        if (name === '') {
          name = '<no name>';
        }
        await startChatBulkAudit({
          projectId,
          auditName: `Audit for ${name} on ${
            date.getMonth() + 1
          }/${date.getDate()}/${date.getFullYear()}`,
          taskIds: attemptIds,
        });
      }
    } catch (err) {
      setErrorMessage('Error creating audit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!contributorId || !attempt) {
    return null;
  }

  return (
    <Modal
      open={open}
      title={`${props.detailType} Details`}
      onClose={onClose}
      className="scale-theme"
    >
      <div className="flex flex-col gap-4">
        {props.detailType === 'Task' && (
          <div>
            <div>
              <Typography.Subtitle3>Task History</Typography.Subtitle3>
            </div>
            <TaskHistoryTable taskInfo={taskInfo} />
          </div>
        )}
        {props.detailType === 'Task' && (
          <div className="flex flex-col gap-2 pt-2">
            <Typography.Subtitle3>Attempt Details</Typography.Subtitle3>
            <div className="grid gap-4" style={{ gridTemplateColumns: '200px 1fr' }}>
              <Typography.Paragraph2 className="text-neutral-500 font-medium col-span-1">
                Time Spent
              </Typography.Paragraph2>
              <Typography.Paragraph2>
                {getTimeWorkedText(attempt.timeSpentSecs)} (
                {claimTimeLoading ? (
                  <Spinner size="sm" />
                ) : claimTimeError ? (
                  'Error'
                ) : (
                  getTimeWorkedText(currentCapForReviewLevel)
                )}{' '}
                cap)
              </Typography.Paragraph2>
              <Typography.Paragraph2 className="text-neutral-500 font-medium">
                Attempt ID
              </Typography.Paragraph2>
              <div className="flex flex-row gap-0.5 items-center">
                <Typography.Paragraph2>{attempt._id}</Typography.Paragraph2>
                <CopyToClipboard text={attempt._id}>
                  <Icon icon={['far', 'copy']} size="sm" />
                </CopyToClipboard>
                <AuditLookupLink to={`/lookup/${attempt._id}`}>
                  <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
                </AuditLookupLink>
              </div>
              <Typography.Paragraph2 className="text-neutral-500 font-medium">
                Task ID
              </Typography.Paragraph2>
              <div className="flex flex-row gap-0.5 items-center">
                <Typography.Paragraph2>{attempt.task ?? ''}</Typography.Paragraph2>
                <CopyToClipboard text={attempt.task ?? ''}>
                  <Icon icon={['far', 'copy']} size="sm" />
                </CopyToClipboard>
                <AuditLookupLink to={`/lookup/${attempt.task}`}>
                  <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
                </AuditLookupLink>
              </div>
              <Typography.Paragraph2 className="text-neutral-500 font-medium">
                Project ID
              </Typography.Paragraph2>
              <div className="flex flex-row gap-0.5 items-center">
                <Typography.Paragraph2>{projectId}</Typography.Paragraph2>
                <CopyToClipboard text={projectId ?? ''}>
                  <Icon icon={['far', 'copy']} size="sm" />
                </CopyToClipboard>
                <AuditLookupLink to={`/lookup/${projectId}`}>
                  <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
                </AuditLookupLink>
              </div>
              <Typography.Paragraph2 className="text-neutral-500 font-medium">
                Review Level
              </Typography.Paragraph2>
              <Typography.Paragraph2>L{attempt.attemptedAtReviewLevel}</Typography.Paragraph2>
            </div>
          </div>
        )}
        {props.detailType === 'Contributor' && (
          <div className="flex flex-col gap-2 pt-2">
            <div className="grid gap-4" style={{ gridTemplateColumns: '200px 1fr' }}>
              <Typography.Paragraph2 className="text-neutral-500 font-medium">
                Contributor
              </Typography.Paragraph2>
              <div className="flex flex-row gap-0.5 items-center">
                <Typography.Paragraph2>{contributor?.email}</Typography.Paragraph2>
                <CopyToClipboard text={contributor?.email ?? ''}>
                  <Icon icon={['far', 'copy']} size="sm" />
                </CopyToClipboard>
                {contributor?._id && (
                  <AuditLookupLink to={`/lookup/${contributor._id}`}>
                    <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
                  </AuditLookupLink>
                )}
              </div>
              <Typography.Paragraph2>Tier/Pod</Typography.Paragraph2>
              <div className="flex flex-row gap-0.5 items-center">
                <Typography.Paragraph2>
                  {podInfoStatus === 'success' ? (
                    podInfo?.teamLeadPodType?.name ?? 'No pod found'
                  ) : (
                    <Spinner size="sm" />
                  )}
                </Typography.Paragraph2>
                {podInfo?.teamLeadPodType?._id && (
                  <AuditPodLeadLink to={`/expert-management?user=${podInfo.manager.id}`}>
                    <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
                  </AuditPodLeadLink>
                )}
              </div>
              <Typography.Paragraph2>SBQ Rate</Typography.Paragraph2>
              <div className="flex flex-row gap-2 items-center">
                <Typography.Paragraph2>
                  {!isLoading ? (
                    !error && !_.isNil(sbqPercent) ? (
                      `${(sbqPercent * 100).toFixed(2)}%`
                    ) : (
                      'Error loading'
                    )
                  ) : (
                    <Spinner size="sm" />
                  )}
                </Typography.Paragraph2>
                <Icon
                  icon="info-circle"
                  size="sm"
                  tooltip={`${sbqRejectedAttempts ?? 0} / ${
                    sbqTotalAttempts ?? 0
                  } tasks SBQ'd in this project`}
                />
              </div>
              <div className="flex">
                <Typography.Paragraph2 className="self-center">
                  Audit Avg Score
                </Typography.Paragraph2>
              </div>
              <div className="flex flex-row gap-2 items-center">
                <Typography.Paragraph2>
                  {auditScoreLoading ? (
                    <Spinner size="sm" />
                  ) : _.isNil(auditAverage) ? (
                    'No tasks audited in past month'
                  ) : error ? (
                    'Error loading'
                  ) : (
                    `${auditAverage.toFixed(2)} (${
                      (numAuditedAttempts ?? 0) >= 50 ? '50+' : numAuditedAttempts ?? 0
                    } tasks)`
                  )}
                </Typography.Paragraph2>
                <div className="flex flex-row items-center gap-0.5">
                  {contributor?._id && (
                    <Button
                      className="hover:underline flex items-center"
                      variant="transparent"
                      onClick={createAudit}
                      disabled={submitting}
                    >
                      <Typography.Paragraph2 className="text-primary-600">
                        Audit 5 more tasks
                      </Typography.Paragraph2>
                      <Icon variant="primary" size="sm" icon="external-link" />
                      {submitting && <Spinner size="sm" />}
                    </Button>
                  )}
                  {errorMessage && <Typography.Paragraph2>{errorMessage}</Typography.Paragraph2>}
                </div>
              </div>
              <Typography.Paragraph2 className="text-neutral-500 font-medium">
                Resume
              </Typography.Paragraph2>
              {contributor?.greenHouseResumeLink ? (
                <a
                  className="hover:underline flex items-center"
                  href={contributor?.greenHouseResumeLink}
                >
                  View Resume
                  <Icon icon="external-link" />
                </a>
              ) : (
                <Typography.Paragraph2>No resume found</Typography.Paragraph2>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default React.memo(ChatBulkAuditDetails);
